import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import * as eventService from "../services/eventService";
import * as guestGroupService from "../services/guestGroupService";
import * as whatsappService from "../services/whatsappService";
import CreateGuestGroupModal, {
  COUNTRY_CODES,
  DEFAULT_COUNTRY_CODE,
  findCountryCodeByCountryName,
  formatPhone,
  normalizeSearch,
} from "../components/guests/CreateGuestGroupModal";

type InvitadoRow = {
  id: number;
  nombre: string;
  apellido: string;
  nombreCompleto: string;
  nombreGrupo: string;
  tipoGrupo: string; // FAMILIA | AMIGOS | INVITADOS_ESPECIALES
  tipoInvitado: string; // PRINCIPAL | ADICIONAL
  telefono: string;
  email: string;
};

type ImportAsistente = {
  row: number;
  nombre: string;
  apellido: string;
  pais: string;
  codigoPais: string;
  celular: string;
  telefono: string;
  correo: string;
  esPrincipal: boolean;
  grupoOriginal: string;
  tipoGrupoOriginal: string;
};

type ImportGroup = {
  nombreGrupo: string;
  tipo: guestGroupService.TipoGrupoInvitados;
  maxInvitados: number;
  adicionalesCantidad: number;
  asistentes: ImportAsistente[];
};

type ImportPreview = {
  fileName: string;
  groups: ImportGroup[];
  errors: string[];
  totalRows: number;
};

type ViewMode = "GRUPOS" | "LISTA";

type EditGuestForm = {
  nombre: string;
  apellido: string;
  codigoPais: string;
  telefono: string;
  email: string;
};

function splitPhoneByCountryCode(phone: string) {
  const normalizedPhone = phone.trim();
  const found = [...COUNTRY_CODES]
    .sort((a, b) => b.code.length - a.code.length)
    .find((option) => normalizedPhone.startsWith(option.code));
  const selected = found ?? COUNTRY_CODES.find((option) => option.code === DEFAULT_COUNTRY_CODE) ?? COUNTRY_CODES[0];

  return {
    codigoPais: selected.code,
    telefono: normalizedPhone.startsWith(selected.code)
      ? normalizedPhone.slice(selected.code.length)
      : normalizedPhone.replace(/^\+/, ""),
  };
}

function etiquetaTipoGrupo(tipo: string) {
  if (tipo === "FAMILIA") return "Familia";
  if (tipo === "AMIGOS") return "Amigos";
  if (tipo === "INVITADOS_ESPECIALES") return "Invitados especiales";
  return tipo;
}

function etiquetaTipoInvitado(relacion: string, esPrincipal?: boolean) {
  // En tu backend viene relacion: PRINCIPAL | OTRO, y esPrincipal boolean
  if (esPrincipal || relacion === "PRINCIPAL") return "Principal";
  return "Adicional";
}

function valueToString(value: unknown) {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toISOString();
  return String(value).trim();
}

function getImportValue(row: Record<string, unknown>, header: string) {
  const wanted = normalizeSearch(header);
  const aliases: Record<string, string[]> = {
    nombres: ["nombres", "nombre"],
    apellidos: ["apellidos", "apellido"],
    pais: ["pais", "país"],
    celular: ["celular", "telefono", "teléfono"],
    correo: ["correo", "email", "e-mail"],
    principal: ["principal", "es principal"],
    grupo: ["grupo"],
    "tipo de grupo": ["tipo de grupo", "tipo grupo", "tipo", "tipo de grup"],
  };
  const normalizedKeys = Object.keys(row).map((key) => ({
    original: key,
    normalized: normalizeSearch(key),
  }));
  const expectedHeaders = aliases[wanted] ?? [wanted];

  const foundKey =
    normalizedKeys.find((key) => expectedHeaders.includes(key.normalized))?.original ??
    (wanted === "tipo de grupo"
      ? normalizedKeys.find((key) => key.normalized.startsWith("tipo de grup"))?.original
      : undefined);

  return foundKey ? valueToString(row[foundKey]) : "";
}

function parsePrincipal(value: string) {
  const normalized = normalizeSearch(value);
  return ["si", "s", "true", "1", "principal", "x"].includes(normalized);
}

function parseTipoGrupo(value: string): guestGroupService.TipoGrupoInvitados | null {
  const normalized = normalizeSearch(value).replace(/[\s-]+/g, "_");
  if (normalized === "familia") return "FAMILIA";
  if (normalized === "amigos") return "AMIGOS";
  if (normalized === "invitados_especiales" || normalized === "especiales" || normalized === "invitados") {
    return "INVITADOS_ESPECIALES";
  }
  return null;
}

function sanitizeCellphone(value: string) {
  return value.trim().replace(/^\+/, "");
}

function buildImportPreview(rows: Record<string, unknown>[], fileName: string): ImportPreview {
  const errors: string[] = [];
  const validGuests: ImportAsistente[] = [];

  rows.forEach((row, idx) => {
    const rowNumber = idx + 2;
    const nombre = getImportValue(row, "Nombres");
    const apellido = getImportValue(row, "Apellidos");
    const pais = getImportValue(row, "Pais");
    const celular = sanitizeCellphone(getImportValue(row, "Celular"));
    const correo = getImportValue(row, "Correo");
    const principalValue = getImportValue(row, "Principal");
    const grupoOriginal = getImportValue(row, "Grupo");
    const tipoGrupoOriginal = getImportValue(row, "Tipo de Grupo");

    if (!nombre) errors.push(`Fila ${rowNumber}: Nombres es obligatorio.`);
    if (!apellido) errors.push(`Fila ${rowNumber}: Apellidos es obligatorio.`);
    if (!pais) errors.push(`Fila ${rowNumber}: Pais es obligatorio.`);
    if (!celular) errors.push(`Fila ${rowNumber}: Celular es obligatorio.`);

    const codigoPais = findCountryCodeByCountryName(pais);
    if (pais && !codigoPais) {
      errors.push(`Fila ${rowNumber}: no se encontró código para el país "${pais}".`);
    }

    if (!nombre || !apellido || !pais || !celular || !codigoPais) return;

    const esGrupoIndividual = !grupoOriginal;
    validGuests.push({
      row: rowNumber,
      nombre,
      apellido,
      pais,
      codigoPais,
      celular,
      telefono: formatPhone(codigoPais, celular),
      correo,
      esPrincipal: esGrupoIndividual ? true : parsePrincipal(principalValue),
      grupoOriginal,
      tipoGrupoOriginal,
    });
  });

  const groupsByName = new Map<string, ImportAsistente[]>();
  validGuests.forEach((guest) => {
    const groupName = guest.grupoOriginal || `${guest.nombre} ${guest.apellido}`.trim();
    groupsByName.set(groupName, [...(groupsByName.get(groupName) ?? []), guest]);
  });

  const groups: ImportGroup[] = [];
  groupsByName.forEach((asistentes, nombreGrupo) => {
    const isSingleGeneratedGroup = asistentes.length === 1 && !asistentes[0].grupoOriginal;
    const principalGuests = asistentes.filter((guest) => guest.esPrincipal);

    if (!isSingleGeneratedGroup && principalGuests.length !== 1) {
      errors.push(`Grupo "${nombreGrupo}": debe tener exactamente 1 invitado principal.`);
      return;
    }

    const principal = isSingleGeneratedGroup ? asistentes[0] : principalGuests[0];
    const tipoGrupoRaw = principal.tipoGrupoOriginal || asistentes.find((guest) => guest.tipoGrupoOriginal)?.tipoGrupoOriginal || "";
    const tipo = isSingleGeneratedGroup ? "AMIGOS" : parseTipoGrupo(tipoGrupoRaw);

    if (!tipo) {
      errors.push(`Grupo "${nombreGrupo}": el invitado principal debe tener Tipo de Grupo válido.`);
      return;
    }

    groups.push({
      nombreGrupo,
      tipo,
      maxInvitados: asistentes.length,
      adicionalesCantidad: Math.max(asistentes.length - 1, 0),
      asistentes,
    });
  });

  return {
    fileName,
    groups,
    errors,
    totalRows: rows.length,
  };
}

function GuestsPage() {
  const navigate = useNavigate();
  const { eventoId } = useParams();
  const idEvento = Number(eventoId);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [eventoNombre, setEventoNombre] = useState<string>("Evento");
  const [eventoFecha, setEventoFecha] = useState<string | null>(null);
  const [eventoActual, setEventoActual] = useState<eventService.EventoAdmin | null>(null);

  const [grupos, setGrupos] = useState<guestGroupService.GrupoInvitados[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("LISTA");
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [isImportMenuOpen, setIsImportMenuOpen] = useState(false);
  const [isReadingExcel, setIsReadingExcel] = useState(false);
  const [isSavingImport, setIsSavingImport] = useState(false);
  const [editingGuest, setEditingGuest] = useState<InvitadoRow | null>(null);
  const [editForm, setEditForm] = useState<EditGuestForm>({
    nombre: "",
    apellido: "",
    codigoPais: DEFAULT_COUNTRY_CODE,
    telefono: "",
    email: "",
  });
  const [editCodigoPaisQuery, setEditCodigoPaisQuery] = useState("Peru +51");
  const [isEditCountryCodeOpen, setIsEditCountryCodeOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [isSavingGuest, setIsSavingGuest] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState<InvitadoRow | null>(null);
  const [deletingGuestId, setDeletingGuestId] = useState<number | null>(null);

  // Envío manual por invitado
  const [sendingId, setSendingId] = useState<number | null>(null);

  // Botón "Enviar invitaciones" (masivo)
  const [isSendAllModalOpen, setIsSendAllModalOpen] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);

  // Notificación simple (toast)
  const [toast, setToast] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  function showToast(type: "ok" | "err", text: string) {
    setToast({ type, text });
    window.setTimeout(() => setToast(null), 3000);
  }

  async function enviarManual(row: InvitadoRow) {
    try {
      setSendingId(row.id);
      await whatsappService.envioManualInvitacion(row.id);
      showToast("ok", `Invitación enviada a ${row.nombreCompleto}.`);
    } catch (e) {
      showToast("err", e instanceof Error ? e.message : "No se pudo enviar la invitación.");
    } finally {
      setSendingId(null);
    }
  }

  async function enviarInvitaciones() {
    if (!Number.isFinite(idEvento)) {
      showToast("err", "No se encontró el evento para enviar invitaciones.");
      return;
    }

    try {
      setSendingAll(true);
      await whatsappService.envioInvitacionesEvento(idEvento);
      try {
        const eventoParaActualizar = eventoActual ?? (await eventService.obtenerEvento(idEvento));
        const updatedEvento = await eventService.actualizarEvento(idEvento, {
          nombre: eventoParaActualizar.nombre,
          slug: eventoParaActualizar.slug,
          fechaPrincipal: eventoParaActualizar.fechaPrincipal,
          fechaLimiteRsvp: eventoParaActualizar.fechaLimiteRsvp,
          zonaHoraria: eventoParaActualizar.zonaHoraria,
          estado: "ENVIADO",
        });
        setEventoActual(updatedEvento);
        setEventoNombre(updatedEvento.nombre);
        setEventoFecha(updatedEvento.fechaPrincipal);
      } catch (estadoError) {
        showToast(
          "err",
          estadoError instanceof Error
            ? `El envío fue iniciado, pero no se pudo marcar el evento como Enviado: ${estadoError.message}`
            : "El envío fue iniciado, pero no se pudo marcar el evento como Enviado."
        );
        setIsSendAllModalOpen(false);
        return;
      }
      setIsSendAllModalOpen(false);
      showToast(
        "ok",
        "El proceso fue iniciado correctamente y el evento fue marcado como Enviado. Para ver el detalle revise RSVP - Evento - Invitaciones enviadas."
      );
    } catch (e) {
      showToast("err", e instanceof Error ? e.message : "No se pudo iniciar el envío de invitaciones.");
    } finally {
      setSendingAll(false);
    }
  }

  function importarDesdeExcel() {
    setIsImportMenuOpen(false);
    fileInputRef.current?.click();
  }

  async function leerExcel(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const isExcel =
      file.name.toLowerCase().endsWith(".xlsx") ||
      file.name.toLowerCase().endsWith(".xls") ||
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel";

    if (!isExcel) {
      showToast("err", "Selecciona un archivo Excel válido (.xlsx o .xls).");
      return;
    }

    try {
      setIsReadingExcel(true);
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) throw new Error("El archivo no contiene hojas.");

      const worksheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { defval: "" });
      const preview = buildImportPreview(rows, file.name);
      setImportPreview(preview);
    } catch (e) {
      showToast("err", e instanceof Error ? e.message : "No se pudo leer el archivo Excel.");
    } finally {
      setIsReadingExcel(false);
    }
  }

  async function guardarImportacion() {
    if (!importPreview || importPreview.errors.length > 0 || !Number.isFinite(idEvento)) return;

    try {
      setIsSavingImport(true);
      const createdGroups: guestGroupService.GrupoInvitados[] = [];

      for (const group of importPreview.groups) {
        const payload: guestGroupService.CreateGrupoInvitadosRequest = {
          eventoId: idEvento,
          nombreGrupo: group.nombreGrupo,
          tipo: group.tipo,
          maxInvitados: group.maxInvitados,
          adicionalesCantidad: group.adicionalesCantidad,
          asistentes: group.asistentes.map((guest) => ({
            nombre: guest.nombre,
            apellido: guest.apellido,
            telefono: guest.telefono,
            email: guest.correo || undefined,
            esPrincipal: guest.esPrincipal,
          })),
        };

        const created = await guestGroupService.crearGrupoInvitados(payload);
        createdGroups.push(created);
      }

      setGrupos((prev) => [...createdGroups, ...prev]);
      setImportPreview(null);
      showToast("ok", `Importación completada: ${createdGroups.length} grupos creados.`);
    } catch (e) {
      showToast("err", e instanceof Error ? e.message : "No se pudo guardar la importación.");
    } finally {
      setIsSavingImport(false);
    }
  }

  async function cargar() {
    if (!Number.isFinite(idEvento)) return;

    setLoading(true);
    setErrorMsg(null);

    try {
      // (opcional) nombre/fecha del evento para el heading, igual que ceremonias
      try {
        const ev = await eventService.obtenerEvento(idEvento);
        setEventoActual(ev);
        setEventoNombre(ev.nombre);
        setEventoFecha(ev.fechaPrincipal);
      } catch {
        // no bloquea invitados
      }

      const data = await guestGroupService.listarGruposInvitadosPorEvento(idEvento);
      setGrupos(data ?? []);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Error cargando invitados");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEvento]);

  // 1) Aplanar grupos -> filas de invitados (para tu tabla/listado)
  const invitadosAll = useMemo<InvitadoRow[]>(() => {
    const rows: InvitadoRow[] = [];
    for (const g of grupos) {
      const asistentes = g.asistentes ?? [];
      for (const a of asistentes) {
        rows.push({
          id: a.id,
          nombre: a.nombre ?? "",
          apellido: a.apellido ?? "",
          nombreCompleto: `${a.nombre ?? ""} ${a.apellido ?? ""}`.trim() || "—",
          nombreGrupo: g.nombreGrupo ?? "—",
          tipoGrupo: g.tipo ?? "—",
          tipoInvitado: etiquetaTipoInvitado(a.relacion, a.esPrincipal),
          telefono: a.telefono ?? "",
          email: a.email ?? "",
        });
      }
    }
    rows.sort((x, y) => x.id - y.id);
    return rows;
  }, [grupos]);

  const filteredEditCountryCodes = useMemo(() => {
    const query = normalizeSearch(editCodigoPaisQuery.trim());
    if (!query) return COUNTRY_CODES;

    return COUNTRY_CODES.filter((option) => {
      const label = normalizeSearch(`${option.country} ${option.code}`);
      return label.includes(query);
    });
  }, [editCodigoPaisQuery]);

  const selectedEditCountryCode = useMemo(() => {
    return COUNTRY_CODES.find((option) => option.code === editForm.codigoPais) ?? COUNTRY_CODES[0];
  }, [editForm.codigoPais]);

  // 2) Filtro por búsqueda
  const filtrados = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return invitadosAll;
    return invitadosAll.filter((r) => {
      return (
        r.nombreCompleto.toLowerCase().includes(query) ||
        r.nombreGrupo.toLowerCase().includes(query) ||
        etiquetaTipoGrupo(r.tipoGrupo).toLowerCase().includes(query) ||
        r.tipoInvitado.toLowerCase().includes(query) ||
        (r.telefono ?? "").toLowerCase().includes(query) ||
        (r.email ?? "").toLowerCase().includes(query)
      );
    });
  }, [invitadosAll, q]);

  const gruposFiltrados = useMemo(() => {
    const query = q.trim().toLowerCase();
    const ordered = [...grupos].sort((x, y) => x.id - y.id);
    if (!query) return ordered;

    return ordered.filter((grupo) => {
      const asistentes = grupo.asistentes ?? [];
      return (
        String(grupo.id).includes(query) ||
        (grupo.nombreGrupo ?? "").toLowerCase().includes(query) ||
        etiquetaTipoGrupo(grupo.tipo ?? "").toLowerCase().includes(query) ||
        asistentes.some((asistente) => {
          const nombreCompleto = `${asistente.nombre ?? ""} ${asistente.apellido ?? ""}`.trim().toLowerCase();
          return (
            nombreCompleto.includes(query) ||
            (asistente.telefono ?? "").toLowerCase().includes(query) ||
            (asistente.email ?? "").toLowerCase().includes(query)
          );
        })
      );
    });
  }, [grupos, q]);

  // 3) Indicadores (cards como ceremonias)
  const stats = useMemo(() => {
    const totalGrupos = grupos.length;
    const totalInvitados = invitadosAll.length;

    const porTipoGrupo = grupos.reduce(
      (acc, g) => {
        acc[g.tipo] = (acc[g.tipo] ?? 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    const principales = invitadosAll.filter((x) => x.tipoInvitado === "Principal").length;
    const adicionales = totalInvitados - principales;

    return {
      totalGrupos,
      totalInvitados,
      principales,
      adicionales,
      porTipoGrupo,
    };
  }, [grupos, invitadosAll]);

  // callbacks del modal
  function onCreated(created: guestGroupService.GrupoInvitados) {
    // mismo patrón que ceremonias: insert y listo
    setGrupos((prev) => [created, ...prev]);
  }

  function editarInvitado(row: InvitadoRow) {
    const phoneParts = splitPhoneByCountryCode(row.telefono);
    const selectedCountry = COUNTRY_CODES.find((option) => option.code === phoneParts.codigoPais) ?? COUNTRY_CODES[0];

    setEditingGuest(row);
    setEditForm({
      nombre: row.nombre,
      apellido: row.apellido,
      codigoPais: phoneParts.codigoPais,
      telefono: phoneParts.telefono,
      email: row.email,
    });
    setEditCodigoPaisQuery(`${selectedCountry.country} ${selectedCountry.code}`);
    setIsEditCountryCodeOpen(false);
    setEditError(null);
  }

  async function guardarInvitadoEditado() {
    if (!editingGuest) return;
    const payload = {
      nombre: editForm.nombre.trim(),
      apellido: editForm.apellido.trim(),
      telefono: formatPhone(editForm.codigoPais, editForm.telefono),
      email: editForm.email.trim(),
    };

    if (!payload.nombre || !payload.apellido || !payload.telefono) {
      setEditError("Nombre, apellido y teléfono son obligatorios.");
      return;
    }

    try {
      setIsSavingGuest(true);
      setEditError(null);
      await guestGroupService.actualizarInvitado(editingGuest.id, {
        nombre: payload.nombre,
        apellido: payload.apellido,
        telefono: payload.telefono,
        email: payload.email || undefined,
      });
      await cargar();
      setEditingGuest(null);
      showToast("ok", "Invitado actualizado correctamente.");
    } catch (e) {
      setEditError(e instanceof Error ? e.message : "No se pudo actualizar el invitado.");
    } finally {
      setIsSavingGuest(false);
    }
  }

  async function eliminarInvitado(row: InvitadoRow) {
    try {
      setDeletingGuestId(row.id);
      await guestGroupService.eliminarInvitado(row.id);
      await cargar();
      setGuestToDelete(null);
      showToast("ok", "Invitado eliminado correctamente.");
    } catch (e) {
      showToast("err", e instanceof Error ? e.message : "No se pudo eliminar el invitado.");
    } finally {
      setDeletingGuestId(null);
    }
  }

  return (
    <>
      {/* Contenedor scrollable interno (idéntico a CeremoniesPage) */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:px-12">
        <div className="max-w-6xl mx-auto flex flex-col gap-8">
          {/* Top: Breadcrumb + Heading */}
          <div className="flex flex-col gap-6">
            {/* Breadcrumbs + volver */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <button
                  type="button"
                  onClick={() => navigate("/home/eventos")}
                  className="text-[#637588] dark:text-[#ab9db8] font-medium hover:text-primary transition-colors flex items-center gap-1"
                >
                  <span className="material-symbols-outlined text-base">arrow_back</span>
                  Eventos
                </button>

                <span className="material-symbols-outlined text-[#637588] dark:text-[#ab9db8] text-base">
                  chevron_right
                </span>

                <span className="text-[#111418] dark:text-white font-semibold">Invitados</span>
              </div>

              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setIsSendAllModalOpen(true)}
                  disabled={sendingAll}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-5 py-2.5 rounded-lg font-bold transition-colors dark:bg-[#191121] dark:hover:bg-white/5 disabled:opacity-60"
                >
                  {sendingAll ? (
                    <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
                  ) : (
                    <span className="material-symbols-outlined text-[18px]">send</span>
                  )}
                  <span>Enviar invitaciones</span>
                </button>

                <button
                  type="button"
                  onClick={() => setOpenCreate(true)}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white px-5 py-2.5 rounded-lg font-bold shadow-lg shadow-primary/20 transition-all active:scale-95"
                >
                  <span className="material-symbols-outlined text-sm font-bold">add</span>
                  <span>Agregar grupo de invitados</span>
                </button>

                <div className="relative flex-1 sm:flex-none">
                  <button
                    type="button"
                    onClick={() => setIsImportMenuOpen((open) => !open)}
                    disabled={isReadingExcel || isSavingImport}
                    className="w-full flex items-center justify-center gap-2 bg-gold-accent/90 hover:bg-gold-accent text-black px-5 py-2.5 rounded-lg font-bold transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                    aria-haspopup="menu"
                    aria-expanded={isImportMenuOpen}
                  >
                    {isReadingExcel ? (
                      <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
                    ) : (
                      <span className="material-symbols-outlined text-[18px]">upload_file</span>
                    )}
                    <span>Importar desde Excel</span>
                    <span className="material-symbols-outlined text-[18px]">expand_more</span>
                  </button>

                  {isImportMenuOpen && (
                    <div className="absolute right-0 z-30 mt-2 w-64 overflow-hidden rounded-xl border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-xl">
                      <a
                        href="/templates/plantilla-importacion-invitados.xlsx"
                        download="plantilla-importacion-invitados.xlsx"
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => setIsImportMenuOpen(false)}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-[#111418] dark:text-white hover:bg-black/5 dark:hover:bg-white/5"
                      >
                        <span className="material-symbols-outlined text-[18px] text-primary">download</span>
                        Descargar plantilla
                      </a>

                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={importarDesdeExcel}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-[#111418] dark:text-white hover:bg-black/5 dark:hover:bg-white/5"
                      >
                        <span className="material-symbols-outlined text-[18px] text-primary">file_open</span>
                        Importar datos
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Heading */}
            <div className="flex flex-col gap-2">
              <h1 className="text-[#111418] dark:text-white text-2xl sm:text-3xl md:text-4xl font-black leading-tight tracking-tight">
                Gestión de invitados:{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-400">
                  {eventoNombre}
                </span>
              </h1>
              <p className="text-[#637588] dark:text-[#ab9db8] text-base font-normal">
                Administra grupos e invitados para este evento.
              </p>
            </div>
          </div>

          {/* Stats row (mismo estilo de ceremonias) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col gap-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
              <div className="flex justify-between items-start">
                <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                  Total de invitados
                </p>
                <span className="material-symbols-outlined text-primary">group</span>
              </div>
              <p className="text-[#111418] dark:text-white text-3xl font-bold leading-tight">
                {stats.totalInvitados}
              </p>
              <div className="text-sm text-[#637588] dark:text-[#ab9db8]">
                {stats.principales} principales • {stats.adicionales} adicionales
              </div>
            </div>

            <div className="flex flex-col gap-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
              <div className="flex justify-between items-start">
                <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                  Total de grupos
                </p>
                <span className="material-symbols-outlined text-primary">diversity_3</span>
              </div>
              <p className="text-[#111418] dark:text-white text-3xl font-bold leading-tight">
                {stats.totalGrupos}
              </p>

              {/* mini breakdown */}
              <div className="text-sm text-[#637588] dark:text-[#ab9db8]">
                {Object.entries(stats.porTipoGrupo).length === 0 ? (
                  "—"
                ) : (
                  <>
                    {(stats.porTipoGrupo["FAMILIA"] ?? 0) > 0 && (
                      <span className="mr-3">Familia: {stats.porTipoGrupo["FAMILIA"]}</span>
                    )}
                    {(stats.porTipoGrupo["AMIGOS"] ?? 0) > 0 && (
                      <span className="mr-3">Amigos: {stats.porTipoGrupo["AMIGOS"]}</span>
                    )}
                    {(stats.porTipoGrupo["INVITADOS_ESPECIALES"] ?? 0) > 0 && (
                      <span>Especiales: {stats.porTipoGrupo["INVITADOS_ESPECIALES"]}</span>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
              <div className="flex justify-between items-start">
                <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                  Fecha del evento
                </p>
                <span className="material-symbols-outlined text-primary">calendar_month</span>
              </div>
              <p className="text-[#111418] dark:text-white text-3xl font-bold leading-tight">
                {eventoFecha ? (
                  new Intl.DateTimeFormat("es-PE", {
                    year: "numeric",
                    month: "short",
                    day: "2-digit",
                  }).format(new Date(eventoFecha))
                ) : (
                  "—"
                )}
              </p>

              {/* bar visual similar */}
              <div className="w-full bg-[#e0e0e0] dark:bg-[#362f40] h-1.5 rounded-full mt-1">
                <div className="bg-primary h-1.5 rounded-full" style={{ width: "55%" }} />
              </div>
            </div>
          </div>

          {/* Toolbar (idéntico a ceremonias) */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white dark:bg-[#231b2e] p-2 rounded-xl border border-[#e0e0e0] dark:border-[#473c53]">
            <div className="relative w-full md:w-auto md:min-w-[300px]">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#637588] dark:text-[#ab9db8]">
                search
              </span>
              <input
                className="w-full bg-[#f7f6f8] dark:bg-[#191121] text-[#111418] dark:text-white pl-10 pr-4 py-2 rounded-lg border-none focus:ring-2 focus:ring-primary placeholder-[#637588] dark:placeholder-[#645970] text-sm"
                placeholder="Buscar invitados o grupos..."
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>

            <div className="flex bg-[#f7f6f8] dark:bg-[#191121] p-1 rounded-lg w-full md:w-auto">
              <button
                type="button"
                onClick={() => setViewMode("GRUPOS")}
                className={
                  viewMode === "GRUPOS"
                    ? "cursor-pointer flex-1 md:flex-none px-4 py-1.5 rounded-md bg-white dark:bg-[#302938] text-primary dark:text-white shadow-sm text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                    : "cursor-pointer flex-1 md:flex-none px-4 py-1.5 rounded-md text-[#637588] dark:text-[#ab9db8] text-sm font-medium flex items-center justify-center gap-2 transition-colors hover:text-primary"
                }
              >
                <span className="material-symbols-outlined text-[18px]">view_module</span>
                <span>Grupos</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode("LISTA")}
                className={
                  viewMode === "LISTA"
                    ? "cursor-pointer flex-1 md:flex-none px-4 py-1.5 rounded-md bg-white dark:bg-[#302938] text-primary dark:text-white shadow-sm text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                    : "cursor-pointer flex-1 md:flex-none px-4 py-1.5 rounded-md text-[#637588] dark:text-[#ab9db8] text-sm font-medium flex items-center justify-center gap-2 transition-colors hover:text-primary"
                }
              >
                <span className="material-symbols-outlined text-[18px]">view_list</span>
                <span>Lista</span>
              </button>
            </div>
          </div>

          {/* Estado */}
          {loading && (
            <div className="text-[#637588] dark:text-[#ab9db8]">Cargando invitados...</div>
          )}
          {!loading && errorMsg && <div className="text-red-400">{errorMsg}</div>}

          {/* Listado (tabla con el mismo “card container” look) */}
          {!loading && !errorMsg && (
            <div className="pb-10">
              {viewMode === "GRUPOS" && (
                <div className="space-y-4">
                  <div className="rounded-xl border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-[#e0e0e0] dark:border-[#362f40] flex items-center justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-bold text-[#111418] dark:text-white">
                          Grupos de invitados
                        </h3>
                        <p className="text-sm text-[#637588] dark:text-[#ab9db8]">
                          Mostrando {gruposFiltrados.length} de {grupos.length} grupos
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={cargar}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors dark:bg-[#191121] dark:hover:bg-white/5"
                      >
                        <span className="material-symbols-outlined text-[18px]">refresh</span>
                        Actualizar
                      </button>
                    </div>

                    <div className="p-5">
                      {gruposFiltrados.length === 0 ? (
                        <div className="px-5 py-10 text-center text-[#637588] dark:text-[#ab9db8]">
                          No se encontraron grupos.
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                          {gruposFiltrados.map((grupo) => {
                            const asistentes = [...(grupo.asistentes ?? [])].sort((x, y) => {
                              if (x.esPrincipal !== y.esPrincipal) return x.esPrincipal ? -1 : 1;
                              return x.id - y.id;
                            });
                            const principal = asistentes.find((asistente) => asistente.esPrincipal || asistente.relacion === "PRINCIPAL");

                            return (
                              <div
                                key={grupo.id}
                                className="rounded-xl border border-[#e0e0e0] dark:border-[#473c53] bg-[#fbfafc] dark:bg-[#191121] overflow-hidden"
                              >
                                <div className="p-4 border-b border-[#e0e0e0] dark:border-[#362f40] flex items-start justify-between gap-4">
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-xs font-bold text-[#637588] dark:text-[#ab9db8]">
                                        #{String(grupo.id).padStart(3, "0")}
                                      </span>
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20">
                                        {etiquetaTipoGrupo(grupo.tipo)}
                                      </span>
                                    </div>
                                    <h4 className="mt-2 text-base font-bold text-[#111418] dark:text-white truncate">
                                      {grupo.nombreGrupo}
                                    </h4>
                                    <p className="mt-1 text-sm text-[#637588] dark:text-[#ab9db8] truncate">
                                      Principal: {principal ? `${principal.nombre} ${principal.apellido}`.trim() : "—"}
                                    </p>
                                  </div>

                                  <div className="text-right shrink-0">
                                    <p className="text-2xl font-bold text-[#111418] dark:text-white">
                                      {asistentes.length}
                                    </p>
                                    <p className="text-xs uppercase tracking-wider text-[#637588] dark:text-[#ab9db8] font-bold">
                                      invitados
                                    </p>
                                  </div>
                                </div>

                                <div className="p-3 divide-y divide-[#e0e0e0] dark:divide-[#362f40]">
                                  {asistentes.map((asistente) => {
                                    const nombreCompleto = `${asistente.nombre ?? ""} ${asistente.apellido ?? ""}`.trim() || "—";
                                    const esPrincipal = asistente.esPrincipal || asistente.relacion === "PRINCIPAL";

                                    return (
                                      <div key={asistente.id} className="py-3 first:pt-0 last:pb-0 flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                          <div className="size-9 rounded-full bg-white dark:bg-[#302938] border border-[#e0e0e0] dark:border-[#473c53] flex items-center justify-center shrink-0">
                                            <span className="material-symbols-outlined text-primary text-[20px]">
                                              {esPrincipal ? "stars" : "person"}
                                            </span>
                                          </div>
                                          <div className="min-w-0">
                                            <p className="text-sm font-semibold text-[#111418] dark:text-white truncate">
                                              {nombreCompleto}
                                            </p>
                                            <p className="text-xs text-[#637588] dark:text-[#ab9db8] truncate">
                                              {asistente.telefono || "Sin teléfono"} · {asistente.email || "Sin correo"}
                                            </p>
                                          </div>
                                        </div>

                                        <span
                                          className={
                                            esPrincipal
                                              ? "inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20 shrink-0"
                                              : "inline-flex items-center px-2.5 py-0.5 rounded-full bg-white/60 dark:bg-white/5 text-[#637588] dark:text-[#ab9db8] text-xs font-bold border border-[#e0e0e0] dark:border-white/10 shrink-0"
                                          }
                                        >
                                          {esPrincipal ? "Principal" : "Adicional"}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {viewMode === "LISTA" && (
                <div className="rounded-xl border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-[#e0e0e0] dark:border-[#362f40] flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-[#111418] dark:text-white">
                      Lista de invitados
                    </h3>
                    <p className="text-sm text-[#637588] dark:text-[#ab9db8]">
                      Mostrando {filtrados.length} de {invitadosAll.length} invitados
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={cargar}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors dark:bg-[#191121] dark:hover:bg-white/5"
                  >
                    <span className="material-symbols-outlined text-[18px]">refresh</span>
                    Actualizar
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-[#f7f6f8] dark:bg-[#191121] text-[#637588] dark:text-[#ab9db8]">
                      <tr>
                        <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">
                          ID
                        </th>
                        <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">
                          Nombre
                        </th>
                        <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">
                          Grupo
                        </th>
                        <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">
                          Tipo de invitado
                        </th>
                        <th className="text-left font-semibold uppercase tracking-wider px-5 py-3">
                          Teléfono
                        </th>
                        <th className="text-right font-semibold uppercase tracking-wider px-5 py-3">
                          Acciones
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-[#e0e0e0] dark:divide-[#362f40]">
                      {filtrados.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-5 py-10 text-center text-[#637588] dark:text-[#ab9db8]"
                          >
                            No se encontraron invitados.
                          </td>
                        </tr>
                      ) : (
                        filtrados.map((r) => (
                          <tr key={r.id} className="hover:bg-black/5 dark:hover:bg-white/5">
                            <td className="px-5 py-4 text-[#111418] dark:text-white font-semibold">
                              #{String(r.id).padStart(3, "0")}
                            </td>

                            <td className="px-5 py-4 text-[#111418] dark:text-white">
                              {r.nombreCompleto}
                              <div className="text-xs text-[#637588] dark:text-[#ab9db8]">
                                {etiquetaTipoGrupo(r.tipoGrupo)}
                              </div>
                            </td>

                            <td className="px-5 py-4 text-[#111418] dark:text-white">
                              {r.nombreGrupo}
                            </td>

                            <td className="px-5 py-4">
                              <span
                                className={
                                  r.tipoInvitado === "Principal"
                                    ? "inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20"
                                    : "inline-flex items-center px-2.5 py-0.5 rounded-full bg-white/5 text-[#637588] dark:text-[#ab9db8] text-xs font-bold border border-white/10"
                                }
                              >
                                {r.tipoInvitado}
                              </span>
                            </td>

                            <td className="px-5 py-4 text-[#111418] dark:text-white">
                              {r.telefono || "—"}
                            </td>

                            <td className="px-5 py-4">
                              <div className="flex items-center justify-end gap-2">
                                {/* Enviar manual */}
                                <button
                                  type="button"
                                  onClick={() => enviarManual(r)}
                                  disabled={sendingId === r.id}
                                  className="p-2 rounded-lg text-[#637588] dark:text-[#ab9db8] hover:bg-[#e0e0e0] dark:hover:bg-[#3d3448] hover:text-primary transition-colors disabled:opacity-60"
                                  title="Enviar invitación"
                                >
                                  {sendingId === r.id ? (
                                    <span className="material-symbols-outlined animate-spin">progress_activity</span>
                                  ) : (
                                    <span className="material-symbols-outlined">send</span>
                                  )}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => editarInvitado(r)}
                                  disabled={isSavingGuest || deletingGuestId === r.id}
                                  className="p-2 rounded-lg text-[#637588] dark:text-[#ab9db8] hover:bg-[#e0e0e0] dark:hover:bg-[#3d3448] hover:text-primary transition-colors"
                                  title="Editar"
                                >
                                  <span className="material-symbols-outlined">edit</span>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => setGuestToDelete(r)}
                                  disabled={deletingGuestId === r.id || isSavingGuest}
                                  className="p-2 rounded-lg text-[#637588] dark:text-[#ab9db8] hover:bg-[#e0e0e0] dark:hover:bg-[#3d3448] hover:text-red-500 transition-colors disabled:opacity-60"
                                  title="Eliminar"
                                >
                                  {deletingGuestId === r.id ? (
                                    <span className="material-symbols-outlined animate-spin">progress_activity</span>
                                  ) : (
                                    <span className="material-symbols-outlined">delete</span>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              )}

              {/* Placeholder como ceremonias */}
              <button
                type="button"
                onClick={() => setOpenCreate(true)}
                className="mt-4 w-full flex flex-col items-center justify-center gap-2 p-8 rounded-xl border-2 border-dashed border-[#e0e0e0] dark:border-[#362f40] bg-transparent hover:bg-white dark:hover:bg-[#231b2e] hover:border-primary transition-all group"
              >
                <div className="size-12 rounded-full bg-[#f7f6f8] dark:bg-[#302938] flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors text-[#637588] dark:text-[#ab9db8]">
                  <span className="material-symbols-outlined">add</span>
                </div>
                <p className="text-[#637588] dark:text-[#ab9db8] font-medium group-hover:text-primary transition-colors">
                  Agregar un nuevo grupo de invitados
                </p>
              </button>
            </div>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        className="hidden"
        onChange={leerExcel}
      />

      {/* Modal crear grupo (ya lo tienes) */}
      <CreateGuestGroupModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        eventoId={idEvento}
        onCreated={onCreated}
      />

      {editingGuest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
            onClick={() => {
              if (!isSavingGuest) setEditingGuest(null);
            }}
            aria-label="Cerrar"
            type="button"
          />

          <div className="relative w-full max-w-xl rounded-2xl border border-white/10 bg-white dark:bg-[#231b2e] shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-[#e0e0e0] dark:border-[#362f40] flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl md:text-2xl font-serif font-semibold text-[#111418] dark:text-white">
                  Actualizar invitado
                </h3>
                <p className="text-sm text-[#637588] dark:text-[#ab9db8] mt-1">
                  Edita solo los datos personales permitidos para #{String(editingGuest.id).padStart(3, "0")}.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setEditingGuest(null)}
                className="text-[#637588] dark:text-[#ab9db8] hover:text-primary transition-colors disabled:opacity-50"
                aria-label="Cerrar"
                disabled={isSavingGuest}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="p-6 space-y-4">
              {editError && (
                <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-sm font-semibold text-red-400">
                  {editError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="flex flex-col gap-2 text-sm font-semibold text-[#111418] dark:text-white">
                  Nombre
                  <input
                    className="bg-[#f7f6f8] dark:bg-[#191121] text-[#111418] dark:text-white px-4 py-3 rounded-lg border border-[#e0e0e0] dark:border-white/10 focus:ring-2 focus:ring-primary outline-none"
                    value={editForm.nombre}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, nombre: e.target.value }))}
                    disabled={isSavingGuest}
                  />
                </label>

                <label className="flex flex-col gap-2 text-sm font-semibold text-[#111418] dark:text-white">
                  Apellido
                  <input
                    className="bg-[#f7f6f8] dark:bg-[#191121] text-[#111418] dark:text-white px-4 py-3 rounded-lg border border-[#e0e0e0] dark:border-white/10 focus:ring-2 focus:ring-primary outline-none"
                    value={editForm.apellido}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, apellido: e.target.value }))}
                    disabled={isSavingGuest}
                  />
                </label>
              </div>

              <div className="flex flex-col gap-2 text-sm font-semibold text-[#111418] dark:text-white">
                <span>Teléfono</span>
                <div className="grid grid-cols-[minmax(0,0.95fr)_minmax(0,1.45fr)] gap-3">
                  <div className="relative">
                    <input
                      className="w-full bg-[#f7f6f8] dark:bg-[#191121] text-[#111418] dark:text-white px-4 py-3 rounded-lg border border-[#e0e0e0] dark:border-white/10 focus:ring-2 focus:ring-primary outline-none"
                      placeholder="Pais o codigo"
                      value={editCodigoPaisQuery}
                      onChange={(e) => {
                        setEditCodigoPaisQuery(e.target.value);
                        setIsEditCountryCodeOpen(true);
                      }}
                      onFocus={() => setIsEditCountryCodeOpen(true)}
                      onBlur={() => {
                        window.setTimeout(() => {
                          setIsEditCountryCodeOpen(false);
                          setEditCodigoPaisQuery(`${selectedEditCountryCode.country} ${selectedEditCountryCode.code}`);
                        }, 120);
                      }}
                      disabled={isSavingGuest}
                      aria-label="Codigo de pais"
                    />

                    {isEditCountryCodeOpen && (
                      <div className="absolute z-20 mt-2 max-h-56 w-full overflow-y-auto rounded-lg border border-[#e0e0e0] dark:border-white/10 bg-white dark:bg-[#231b2e] shadow-xl">
                        {filteredEditCountryCodes.length === 0 && (
                          <div className="px-3 py-2 text-sm text-[#637588] dark:text-[#ab9db8]">
                            Sin resultados
                          </div>
                        )}

                        {filteredEditCountryCodes.map((option) => (
                          <button
                            type="button"
                            key={`${option.country}-${option.code}`}
                            className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm text-[#111418] dark:text-slate-200 hover:bg-black/5 dark:hover:bg-white/10"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setEditForm((prev) => ({ ...prev, codigoPais: option.code }));
                              setEditCodigoPaisQuery(`${option.country} ${option.code}`);
                              setIsEditCountryCodeOpen(false);
                            }}
                          >
                            <span className="truncate">{option.country}</span>
                            <span className="font-semibold text-primary">{option.code}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <input
                    className="w-full bg-[#f7f6f8] dark:bg-[#191121] text-[#111418] dark:text-white px-4 py-3 rounded-lg border border-[#e0e0e0] dark:border-white/10 focus:ring-2 focus:ring-primary outline-none"
                    placeholder="Numero"
                    value={editForm.telefono}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, telefono: e.target.value }))}
                    disabled={isSavingGuest}
                    aria-label="Numero de telefono"
                  />
                </div>
              </div>

              <label className="flex flex-col gap-2 text-sm font-semibold text-[#111418] dark:text-white">
                Email
                <input
                  type="email"
                  className="bg-[#f7f6f8] dark:bg-[#191121] text-[#111418] dark:text-white px-4 py-3 rounded-lg border border-[#e0e0e0] dark:border-white/10 focus:ring-2 focus:ring-primary outline-none"
                  placeholder="Correo (opcional)"
                  value={editForm.email}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                  disabled={isSavingGuest}
                />
              </label>
            </div>

            <div className="p-6 border-t border-[#e0e0e0] dark:border-[#362f40] flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditingGuest(null)}
                disabled={isSavingGuest}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-[#111418] dark:text-slate-200 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 border border-[#e0e0e0] dark:border-white/10 transition-colors disabled:opacity-60"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={guardarInvitadoEditado}
                disabled={isSavingGuest}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {isSavingGuest && (
                  <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
                )}
                {isSavingGuest ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isSendAllModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
            onClick={() => {
              if (!sendingAll) setIsSendAllModalOpen(false);
            }}
            aria-label="Cerrar"
            type="button"
          />

          <div className="relative w-full max-w-2xl rounded-2xl border border-gold-accent/20 bg-white dark:bg-[#231b2e] shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-[#e0e0e0] dark:border-[#362f40] flex items-start gap-4">
              <div className="size-11 rounded-xl bg-gold-accent/10 border border-gold-accent/20 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-gold-accent">campaign</span>
              </div>

              <div className="min-w-0">
                <h3 className="text-xl md:text-2xl font-serif font-semibold text-[#111418] dark:text-white">
                  Enviar invitaciones
                </h3>
                <p className="text-sm text-[#637588] dark:text-[#ab9db8] mt-1">
                  Se iniciará el envío masivo para {eventoNombre}.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsSendAllModalOpen(false)}
                className="ml-auto text-[#637588] dark:text-[#ab9db8] hover:text-primary transition-colors disabled:opacity-50"
                aria-label="Cerrar"
                disabled={sendingAll}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="rounded-xl border border-[#e0e0e0] dark:border-[#473c53] bg-[#f7f6f8] dark:bg-[#191121] p-4">
                <ul className="space-y-3 text-sm text-[#637588] dark:text-[#ab9db8]">
                  <li className="flex gap-3">
                    <span className="material-symbols-outlined text-primary text-[20px] shrink-0">group</span>
                    <span>Se realizará el envío de invitaciones a todos los invitados principales registrados.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="material-symbols-outlined text-primary text-[20px] shrink-0">schedule</span>
                    <span>
                      Es un proceso asíncrono que mandará mensajes de WhatsApp a cada invitado principal, por lo que
                      podría tardar un poco en terminar de enviar la invitación a todos los invitados.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="material-symbols-outlined text-red-500 text-[20px] shrink-0">warning</span>
                    <span>Es una acción que no se puede revertir, por lo que deberá estar seguro(a) de hacerlo.</span>
                  </li>
                </ul>
              </div>

              <div className="rounded-xl border border-gold-accent/20 bg-gold-accent/10 p-4">
                <p className="text-sm font-semibold text-[#111418] dark:text-white">
                  ¿Quiere realizar el envío masivo de mensajes para este evento?
                </p>
              </div>

              {sendingAll && (
                <div className="rounded-xl border border-primary/20 bg-primary/10 p-4 flex items-center gap-3 text-sm font-semibold text-primary">
                  <span className="material-symbols-outlined text-[20px] animate-spin">progress_activity</span>
                  Iniciando el proceso de envío de invitaciones...
                </div>
              )}
            </div>

            <div className="p-6 border-t border-[#e0e0e0] dark:border-[#362f40] flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsSendAllModalOpen(false)}
                disabled={sendingAll}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-[#111418] dark:text-slate-200 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 border border-[#e0e0e0] dark:border-white/10 transition-colors disabled:opacity-60"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={enviarInvitaciones}
                disabled={sendingAll}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {sendingAll && (
                  <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
                )}
                {sendingAll ? "Iniciando..." : "Confirmar envío"}
              </button>
            </div>
          </div>
        </div>
      )}

      {guestToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
            onClick={() => {
              if (deletingGuestId === null) setGuestToDelete(null);
            }}
            aria-label="Cerrar"
            type="button"
          />

          <div className="relative w-full max-w-lg rounded-2xl border border-red-500/20 bg-white dark:bg-[#231b2e] shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-[#e0e0e0] dark:border-[#362f40] flex items-start gap-4">
              <div className="size-11 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-red-500">warning</span>
              </div>

              <div className="min-w-0">
                <h3 className="text-xl md:text-2xl font-serif font-semibold text-[#111418] dark:text-white">
                  Eliminar invitado
                </h3>
                <p className="text-sm text-[#637588] dark:text-[#ab9db8] mt-1">
                  Esta acción no se puede deshacer.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setGuestToDelete(null)}
                className="ml-auto text-[#637588] dark:text-[#ab9db8] hover:text-primary transition-colors disabled:opacity-50"
                aria-label="Cerrar"
                disabled={deletingGuestId !== null}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="rounded-xl border border-[#e0e0e0] dark:border-[#473c53] bg-[#f7f6f8] dark:bg-[#191121] p-4">
                <p className="text-sm text-[#637588] dark:text-[#ab9db8]">Invitado seleccionado</p>
                <p className="mt-1 text-lg font-bold text-[#111418] dark:text-white">
                  {guestToDelete.nombreCompleto}
                </p>
                <p className="mt-1 text-sm text-[#637588] dark:text-[#ab9db8]">
                  #{String(guestToDelete.id).padStart(3, "0")} · {guestToDelete.nombreGrupo}
                </p>
              </div>

              <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4">
                <div className="flex gap-3">
                  <span className="material-symbols-outlined text-red-500 text-[20px] shrink-0">delete_forever</span>
                  <p className="text-sm font-semibold text-red-500">
                    Se eliminará definitivamente este invitado y todos sus datos relacionados, incluyendo RSVP, logs y registros asociados.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-[#e0e0e0] dark:border-[#362f40] flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
              <button
                type="button"
                onClick={() => setGuestToDelete(null)}
                disabled={deletingGuestId !== null}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-[#111418] dark:text-slate-200 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 border border-[#e0e0e0] dark:border-white/10 transition-colors disabled:opacity-60"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={() => eliminarInvitado(guestToDelete)}
                disabled={deletingGuestId !== null}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-60"
              >
                {deletingGuestId !== null && (
                  <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
                )}
                {deletingGuestId !== null ? "Eliminando..." : "Eliminar definitivamente"}
              </button>
            </div>
          </div>
        </div>
      )}

      {importPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
            onClick={() => {
              if (!isSavingImport) setImportPreview(null);
            }}
            aria-label="Cerrar"
            type="button"
          />

          <div className="relative w-full max-w-6xl max-h-[90vh] rounded-2xl border border-white/10 bg-white dark:bg-[#231b2e] shadow-2xl overflow-hidden flex flex-col">
            <div className="p-6 border-b border-[#e0e0e0] dark:border-[#362f40] flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl md:text-2xl font-serif font-semibold text-[#111418] dark:text-white">
                  Resumen de importación
                </h3>
                <p className="text-sm text-[#637588] dark:text-[#ab9db8] mt-1">
                  {importPreview.fileName} · {importPreview.totalRows} filas leídas · {importPreview.groups.length} grupos preparados
                </p>
              </div>

              <button
                type="button"
                onClick={() => setImportPreview(null)}
                className="text-[#637588] dark:text-[#ab9db8] hover:text-primary transition-colors disabled:opacity-50"
                aria-label="Cerrar"
                disabled={isSavingImport}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="p-6 overflow-y-auto min-h-0 space-y-5">
              {importPreview.errors.length > 0 && (
                <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-4">
                  <p className="text-sm font-bold text-red-400 mb-2">
                    Corrige estas validaciones antes de guardar
                  </p>
                  <div className="max-h-36 overflow-y-auto space-y-1">
                    {importPreview.errors.map((error, idx) => (
                      <p key={`${error}-${idx}`} className="text-sm text-red-300">
                        {error}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl border border-[#e0e0e0] dark:border-[#473c53] p-4">
                  <p className="text-xs uppercase tracking-wider text-[#637588] dark:text-[#ab9db8] font-bold">
                    Grupos
                  </p>
                  <p className="text-2xl font-bold text-[#111418] dark:text-white">{importPreview.groups.length}</p>
                </div>
                <div className="rounded-xl border border-[#e0e0e0] dark:border-[#473c53] p-4">
                  <p className="text-xs uppercase tracking-wider text-[#637588] dark:text-[#ab9db8] font-bold">
                    Invitados
                  </p>
                  <p className="text-2xl font-bold text-[#111418] dark:text-white">
                    {importPreview.groups.reduce((total, group) => total + group.asistentes.length, 0)}
                  </p>
                </div>
                <div className="rounded-xl border border-[#e0e0e0] dark:border-[#473c53] p-4">
                  <p className="text-xs uppercase tracking-wider text-[#637588] dark:text-[#ab9db8] font-bold">
                    Validaciones
                  </p>
                  <p className={importPreview.errors.length > 0 ? "text-2xl font-bold text-red-400" : "text-2xl font-bold text-green-500"}>
                    {importPreview.errors.length}
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-[#e0e0e0] dark:border-[#473c53]">
                <table className="min-w-full text-sm">
                  <thead className="bg-[#f7f6f8] dark:bg-[#191121] text-[#637588] dark:text-[#ab9db8]">
                    <tr>
                      <th className="text-left font-semibold uppercase tracking-wider px-4 py-3">Grupo</th>
                      <th className="text-left font-semibold uppercase tracking-wider px-4 py-3">Tipo</th>
                      <th className="text-left font-semibold uppercase tracking-wider px-4 py-3">Invitado</th>
                      <th className="text-left font-semibold uppercase tracking-wider px-4 py-3">Pais</th>
                      <th className="text-left font-semibold uppercase tracking-wider px-4 py-3">Teléfono</th>
                      <th className="text-left font-semibold uppercase tracking-wider px-4 py-3">Correo</th>
                      <th className="text-left font-semibold uppercase tracking-wider px-4 py-3">Principal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e0e0e0] dark:divide-[#362f40]">
                    {importPreview.groups.flatMap((group) =>
                      group.asistentes.map((guest) => (
                        <tr key={`${group.nombreGrupo}-${guest.row}`} className="hover:bg-black/5 dark:hover:bg-white/5">
                          <td className="px-4 py-3 text-[#111418] dark:text-white font-semibold">
                            {group.nombreGrupo}
                          </td>
                          <td className="px-4 py-3 text-[#111418] dark:text-white">
                            {etiquetaTipoGrupo(group.tipo)}
                          </td>
                          <td className="px-4 py-3 text-[#111418] dark:text-white">
                            {guest.nombre} {guest.apellido}
                          </td>
                          <td className="px-4 py-3 text-[#111418] dark:text-white">
                            {guest.pais} <span className="text-[#637588] dark:text-[#ab9db8]">{guest.codigoPais}</span>
                          </td>
                          <td className="px-4 py-3 text-[#111418] dark:text-white">{guest.telefono}</td>
                          <td className="px-4 py-3 text-[#111418] dark:text-white">{guest.correo || "—"}</td>
                          <td className="px-4 py-3">
                            <span
                              className={
                                guest.esPrincipal
                                  ? "inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20"
                                  : "inline-flex items-center px-2.5 py-0.5 rounded-full bg-white/5 text-[#637588] dark:text-[#ab9db8] text-xs font-bold border border-white/10"
                              }
                            >
                              {guest.esPrincipal ? "Si" : "No"}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="p-6 border-t border-[#e0e0e0] dark:border-[#362f40] flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setImportPreview(null)}
                disabled={isSavingImport}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-[#111418] dark:text-slate-200 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 border border-[#e0e0e0] dark:border-white/10 transition-colors disabled:opacity-60"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={guardarImportacion}
                disabled={isSavingImport || importPreview.errors.length > 0 || importPreview.groups.length === 0}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSavingImport && (
                  <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
                )}
                {isSavingImport ? "Guardando..." : "Guardar importación"}
              </button>
            </div>
          </div>
        </div>
      )}

      {(isReadingExcel || isSavingImport || isSavingGuest || deletingGuestId !== null) && (
        <div className="fixed inset-0 z-[60] pointer-events-none flex items-center justify-center">
          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#191121]/90 px-5 py-4 text-white shadow-2xl">
            <span className="material-symbols-outlined animate-spin">progress_activity</span>
            <span className="font-semibold">
              {isReadingExcel
                ? "Leyendo Excel..."
                : isSavingImport
                  ? "Guardando grupos..."
                  : isSavingGuest
                    ? "Actualizando invitado..."
                    : "Eliminando invitado..."}
            </span>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed top-6 right-6 z-50">
          <div
            className={`flex items-center gap-2 px-4 py-3 rounded-xl border shadow-lg ${toast.type === "ok"
                ? "bg-white dark:bg-[#231b2e] border-green-300/30 dark:border-green-700/30"
                : "bg-white dark:bg-[#231b2e] border-red-300/30 dark:border-red-700/30"
              }`}
          >
            <span
              className={`material-symbols-outlined ${toast.type === "ok" ? "text-green-500" : "text-red-500"
                }`}
            >
              {toast.type === "ok" ? "check_circle" : "error"}
            </span>
            <p className="text-sm text-[#111418] dark:text-white font-semibold">{toast.text}</p>
          </div>
        </div>
      )}
    </>
  );
}

export default GuestsPage;
